import React, { useEffect } from 'react';
import { CheckCircle } from 'lucide-react';

const CourierToUKPage = () => {
  useEffect(() => {
    document.title = 'Send Courier from India to UK | International Parcel Shipping | XFas Logistics';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Reliable courier service from India to UK. Send documents, parcels & cargo with XFas Logistics — competitive rates, express & economy options. Get a quote.');
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gray-900 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-5xl mb-4">🇬🇧</div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Send Courier from India to the UK</h1>
          <p className="text-xl text-orange-400 font-medium">Trusted, Affordable, Door-to-Door</p>
          <p className="mt-4 text-gray-300 text-lg">Seamless Shipping from India to England, Scotland, Wales & Northern Ireland</p>
          <a href="/#get-quote" className="inline-block mt-8 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors">
            Get a Quote
          </a>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-16 space-y-16">

        {/* Why XFas */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose XFas for India to UK Shipping?</h2>
          <ul className="space-y-3">
            {[
              ['✈️', 'Air Express delivery in 2–3 business days to any UK postcode'],
              ['💷', 'Standard shipping in 5–7 business days'],
              ['📦', 'All parcel types handled — personal gifts, Indian food & grocery (permissible items), textiles, electronics, documents'],
              ['🏠', 'True door-to-door service across all UK regions including remote Scottish Highlands and Northern Ireland'],
              ['🔄', 'Return logistics available for e-commerce brands with UK customer bases'],
            ].map(([icon, text]) => (
              <li key={text} className="flex gap-3 text-gray-600">
                <span className="flex-shrink-0">{icon}</span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Shipping Options */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Shipping Options: India to UK</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="text-left p-4 rounded-tl-lg">Service</th>
                  <th className="text-left p-4">Transit Time</th>
                  <th className="text-left p-4 rounded-tr-lg">Best For</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Air Express', '2–3 Business Days', 'Urgent shipments, business documents'],
                  ['Standard', '5–7 Business Days', 'Personal parcels, gifts'],
                  ['Cargo / Commercial', 'Customised', 'Bulk trade shipments, garment exporters'],
                ].map(([service, time, best], i) => (
                  <tr key={service} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-4 font-semibold text-gray-800">{service}</td>
                    <td className="p-4 text-gray-600">{time}</td>
                    <td className="p-4 text-gray-600">{best}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* What Can You Ship */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What Can You Ship to the UK?</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              'Personal parcels and gifts',
              'Indian textiles, sarees, and handicrafts',
              'Ayurvedic products and personal care items (subject to UK regulations)',
              'Business documents and contracts',
              'Commercial samples and trade goods',
              'Books, educational materials, and media',
            ].map((item) => (
              <div key={item} className="flex gap-3 items-start">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Popular Routes */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Popular India–UK Shipping Routes</h2>
          <p className="text-gray-600 leading-relaxed">
            We regularly serve shippers from Mumbai, Delhi, Bengaluru, Chennai, Hyderabad, Ahmedabad, Surat, and Tirupur — delivering to London, Birmingham, Leicester, Manchester, Leeds, Glasgow, and everywhere in between.
          </p>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[
              {
                q: 'How much does it cost to send a parcel from India to the UK?',
                a: 'Pricing depends on weight, dimensions, and service type. XFas offers competitive, transparent pricing with no hidden surcharges. Get an instant quote on our website.',
              },
              {
                q: 'Do I need to pay customs duty on shipments to the UK?',
                a: 'The UK applies import duties based on the commodity type under the UK Global Tariff. Items below £135 may be subject to import VAT but are duty-free. XFas will advise you on the applicable rates for your specific goods.',
              },
              {
                q: 'Can I send food items from India to the UK?',
                a: 'Certain processed and packaged food items are permissible. Fresh produce, meat, and dairy are generally restricted. XFas will confirm what is allowed based on current UK Border Force rules.',
              },
            ].map(({ q, a }) => (
              <div key={q} className="border-b border-gray-200 pb-6">
                <p className="font-semibold text-gray-800 mb-2">{q}</p>
                <p className="text-gray-600">{a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Related links */}
        <section className="bg-gray-50 rounded-2xl p-6">
          <p className="text-gray-600 text-sm">Also shipping to: <a href="/send-courier-from-india-to-usa/" className="text-orange-500 hover:underline font-medium">United States</a> · <a href="/send-courier-from-india-to-europe/" className="text-orange-500 hover:underline font-medium">Europe</a></p>
        </section>

        {/* CTA */}
        <section className="text-center bg-orange-500 rounded-2xl py-12 px-6 text-white">
          <h2 className="text-2xl font-bold mb-3">Ready to Ship to the UK?</h2>
          <p className="mb-6 text-orange-100">Get an instant quote and compare carrier rates in seconds.</p>
          <a href="/#get-quote" className="inline-block bg-white text-orange-500 font-semibold px-8 py-3 rounded-lg hover:bg-orange-50 transition-colors">
            Get a Quote
          </a>
        </section>

      </div>
    </div>
  );
};

export default CourierToUKPage;
